import React from 'react'
import moment from 'moment'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import ScheduleIcon from '@mui/icons-material/Schedule'
import {SEMANTIC} from 'src/theme-new/tokens'
import {MaterialRecord, LinkRecord} from 'src/pages-new/infoMaterials/columns'
import {InfoMaterialItem, Dosage, SecondaryTherapyItem, DocumentItem} from 'src/pages-new/homepage/types'

export const formatItemDate = (value?: string): string => {
  if (!value) return ''
  const m = moment(value)
  return m.hours() || m.minutes()
    ? m.format('DD MMM. YYYY HH:mm')
    : m.format('DD MMM. YYYY')
}

export const formatDosage = (dosage?: Dosage): string =>
  dosage?.value !== undefined && dosage?.value !== null
    ? `${dosage.value} ${dosage.unitType ?? ''}`.trim()
    : ''

const DOSAGE_FIELDS: Array<{key: keyof SecondaryTherapyItem; label: string}> = [
  {key: 'assumptionDosage', label: 'Dose/assunzione'},
  {key: 'drugDosage', label: 'Dosaggio farmaco'},
  {key: 'dailyDosage', label: 'Dose giornaliera'},
  {key: 'weeklyDosage', label: 'Dose settimanale'},
  {key: 'monthlyDosage', label: 'Dose mensile'},
  {key: 'vialDosage', label: 'Dose per fiala'},
]

/** Righe dosaggio valorizzate di una terapia accessoria, nell'ordine di `DOSAGE_FIELDS`;
 * esclude i dosaggi assenti (non esiste un singolo campo `dosage` su questa risorsa). */
export const getDosageRows = (therapy: SecondaryTherapyItem): Array<{label: string; value: string}> =>
  DOSAGE_FIELDS
    .map(({key, label}) => ({label, value: formatDosage(therapy[key] as Dosage | undefined)}))
    .filter(row => row.value !== '')

export const getPerformanceStatusIcon = (state?: string): React.ReactNode => {
  if (!state) return null
  const s = state.toLowerCase()
  if (s === 'erogato')
    return React.createElement(CheckCircleIcon, {sx: {fontSize: 14, color: SEMANTIC.success.main, flexShrink: 0}})
  if (s === 'non erogato')
    return React.createElement(CancelIcon, {sx: {fontSize: 14, color: SEMANTIC.error.main, flexShrink: 0}})
  return React.createElement(ScheduleIcon, {sx: {fontSize: 14, color: SEMANTIC.warning.main, flexShrink: 0}})
}

const toMaterialItem = (m: MaterialRecord): InfoMaterialItem => ({
  kind: 'material',
  id: m._id,
  title: m.name ?? m.fileName ?? '—',
  date: m.createdAt,
  fileId: m.fileId,
  fileName: m.fileName,
})

const toLinkItem = (l: LinkRecord): InfoMaterialItem => ({
  kind: 'link',
  id: l._id,
  title: l.name ?? l.url ?? '—',
  date: l.createdAt,
  url: l.url,
})

/**
 * Metà slot ai materiali e metà ai link; gli slot rimasti liberi (perché una
 * delle due liste è più corta) vengono riempiti prima con i materiali extra,
 * poi con i link extra. Materiali sempre prima dei link nell'output.
 */
export const mergeInfoMaterialsItems = (
  materials: MaterialRecord[],
  links: LinkRecord[],
  max: number,
): InfoMaterialItem[] => {
  const half = Math.floor(max / 2)
  let matCount = Math.min(materials.length, half)
  let linkCount = Math.min(links.length, half)

  let free = max - matCount - linkCount
  const extraMat = Math.min(free, materials.length - matCount)
  matCount += extraMat
  free -= extraMat
  linkCount += Math.min(free, links.length - linkCount)

  return [
    ...materials.slice(0, matCount).map(toMaterialItem),
    ...links.slice(0, linkCount).map(toLinkItem),
  ]
}

/**
 * Card "I miei documenti": documenti personali e condivisi (materiali del programma)
 * uniti in un'unica lista, dal più recente, troncata a `max`.
 */
export const mergeMyDocumentsItems = (
  personal: DocumentItem[],
  shared: MaterialRecord[],
  max: number,
): DocumentItem[] => {
  const sharedItems: DocumentItem[] = shared.map(m => ({
    fileId: m.fileId,
    name: m.name,
    fileName: m.fileName,
    createdAt: m.createdAt,
  }))
  const time = (d: DocumentItem) => (d.createdAt ? new Date(d.createdAt).getTime() : 0)
  return [...personal, ...sharedItems]
    .sort((a, b) => time(b) - time(a))
    .slice(0, max)
}

/** Sottotitolo di un link: hostname senza "www." (fallback: url grezzo). */
export const formatLinkSubtitle = (url?: string): string => {
  if (!url) return ''
  try {
    const {hostname} = new URL(url.startsWith('http') ? url : `https://${url}`)
    return hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}
